// Client estimate PDF layout
import { CUR, ccyHeader, secTotals } from '../core/budget.js';
import { useEffect, useRef, useState } from '../core/react.js';
import { audit } from '../core/audit.js';
import { sentenceCase } from '../core/format.js';

const EST = {
  W: 595.28,
  H: 841.89,
  LEFT: 72.5,
  AMT_RIGHT: 452.6,
  TITLE_X: 216.8,
  CCY_X: 406.6,
  DESC_W: 322,
  TITLE_W: 182,
  SIZE: 12,
  LINE: 13.5,
  BOTTOM: 785,
  ASC: 9.6,
  STRIP: {
    x: 452.8,
    y: -6.4,
    w: 128.1,
    h: 843.3
  }
};
// The PDF fonts and the letterhead strip and signature (public/assets.js) arrive with the PDF maker (loadJsPDF),
// not with the app: most visits never make a PDF.
const asset = k => (window.GB_ASSETS || {})[k];
const EST_CHARS = /[^\u0020-\u007E\u00A0-\u017F\u2013\u2014\u2018-\u201E\u2022\u2026\u20AC\u2122\n]/g;
export function estRegisterFont(doc) {
  doc.addFileToVFS('EstSans-Regular.ttf', asset('fontRegular'));
  doc.addFont('EstSans-Regular.ttf', 'EstSans', 'normal');
  doc.addFileToVFS('EstSans-Bold.ttf', asset('fontBold'));
  doc.addFont('EstSans-Bold.ttf', 'EstSans', 'bold');
}
const EST_ENTITIES = {
  glassbox: {
    company: 'Glassbox Design Ltd',
    letterhead: true
  }
};
function ordinalDate(iso) {
  const d = iso ? new Date(iso + 'T12:00:00') : new Date();
  if (isNaN(d)) return '';
  const n = d.getDate(),
    s = n % 100 >= 11 && n % 100 <= 13 ? 'th' : {
      1: 'st',
      2: 'nd',
      3: 'rd'
    }[n % 10] || 'th';
  return `${n}${s} ${d.toLocaleDateString('en-GB', {
    month: 'long'
  })} ${d.getFullYear()}`;
}
// The signature above the name: the built-in one (meta.signature === true, the signer set on the server), or the
// person's own uploaded image ({ img, w, h }), fitted into the space above the name keeping its shape.
function signatureOp(sig, y) {
  if (sig && sig.img) {
    const k = Math.min(170 / sig.w, 50 / sig.h), w = sig.w * k, h = sig.h * k;
    return { t: 'img', key: 'usersig', src: sig.img, x: EST.LEFT - 2, y: y + 53 - h, w, h };
  }
  return { t: 'img', key: 'sig', x: 24.6, y: y - 14.6, w: 195.8, h: 110.3 };
}
// the PDF font has £, $ and € only
const PDF_SYM = { GBP: '£', USD: '$', EUR: '€' };
export const estMoney = n => (n < 0 ? '-' : '') + (PDF_SYM[CUR.bill] || '') + Math.abs(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
let _estMeasure = null;
function estWrap(text, width, bold) {
  // the PDF maker and its fonts arrive as two files (loadJsPDF): measure with the fonts only once both are here, or
  // jsPDF fails on the missing font and takes the page down; until then an estimate by character count
  if (!window.jspdf || !asset('fontRegular') || !asset('fontBold')) {
    const max = Math.floor(width / 5.6),
      out = [];
    String(text || '').split(' ').forEach(w => {
      const l = out.length ? out[out.length - 1] : null;
      if (l !== null && (l + ' ' + w).length <= max) out[out.length - 1] = l + ' ' + w;else out.push(w);
    });
    return out.length ? out : [''];
  }
  if (!_estMeasure) {
    const {
      jsPDF
    } = window.jspdf;
    _estMeasure = new jsPDF({
      unit: 'pt',
      format: 'a4'
    });
    estRegisterFont(_estMeasure);
    _estMeasure.setFontSize(EST.SIZE);
  }
  _estMeasure.setFont('EstSans', bold ? 'bold' : 'normal');
  return _estMeasure.splitTextToSize(String(text || ''), width);
}
export function buildEstimate({
  sections,
  fx,
  meta
}) {
  const ent = EST_ENTITIES[meta.entity] || EST_ENTITIES.glassbox;
  const pages = [];
  let ops, y;
  const newPage = () => {
    ops = [];
    if (ent.letterhead) ops.push({
      t: 'img',
      key: 'strip',
      ...EST.STRIP
    });else ops.push({
      t: 'text',
      x: 470,
      y: 65.2 + EST.ASC,
      s: ent.company,
      bold: true
    });
    pages.push(ops);
    y = 65.2;
  };
  const text = (x, s, o = {}) => ops.push({
    t: 'text',
    x,
    y: y + EST.ASC,
    s,
    ...o
  });
  newPage();
  text(EST.LEFT, ordinalDate(meta.date));
  y = 120.4;
  String(meta.recipient || '').split('\n').map(l => l.trim()).filter(Boolean).forEach(l => {
    text(EST.LEFT, l);
    y += EST.LINE;
  });
  y = Math.max(272.6, y + 40);
  const titleLines = estWrap(meta.title || '', EST.TITLE_W, true);
  text(EST.LEFT, 'Estimate:', {
    bold: true
  });
  text(EST.CCY_X, ccyHeader(CUR.bill), {
    bold: true
  });
  titleLines.forEach((l, i) => ops.push({
    t: 'text',
    x: EST.TITLE_X,
    y: y + EST.ASC + i * EST.LINE,
    s: l,
    bold: true
  }));
  y += 27.1 + (titleLines.length - 1) * EST.LINE;
  let total = 0,
    first = true;
  const estLines = [];
  sections.forEach(sec => {
    const items = secTotals(sec.items, fx).items.filter(ci => ci.st !== 'Cancelled');
    if (!items.length) return;
    if (!first) y += EST.LINE;
    first = false;
    if (y + 25 + EST.LINE > EST.BOTTOM) {
      newPage();
      y = 72.5;
    }
    text(EST.LEFT, meta.headings[sec.id] || sentenceCase(sec.name), {
      bold: true
    });
    y += 25;
    items.forEach(ci => {
      const lines = estWrap(ci.desc, EST.DESC_W, false);
      const h = lines.length * EST.LINE;
      if (y + h > EST.BOTTOM) {
        newPage();
        y = 72.5;
      }
      lines.forEach((l, i) => ops.push({
        t: 'text',
        x: EST.LEFT,
        y: y + EST.ASC + i * EST.LINE,
        s: l
      }));
      const amt = Math.round(ci.outUSD * 100) / 100;
      estLines.push({
        secId: sec.id,
        sec: meta.headings[sec.id] || sentenceCase(sec.name),
        itemId: ci.id,
        desc: ci.desc,
        amt
      });
      text(EST.AMT_RIGHT, estMoney(amt), {
        right: true
      });
      total = Math.round((total + amt) * 100) / 100;
      y += h;
    });
  });
  y += 13.5;
  const vat = meta.vat && meta.vat.on && Number(meta.vat.rate) > 0 ? Number(meta.vat.rate) / 100 : 0;
  const vatAmt = Math.round(total * vat * 100) / 100;
  const termLines = String(meta.terms || '').trim() ? String(meta.terms).trim().split('\n').flatMap(p => p.trim() ? estWrap(p.trim(), EST.AMT_RIGHT - EST.LEFT, false) : ['']) : [];
  if (y + 178 + (vat ? 2 * EST.LINE + 8 : 0) > EST.BOTTOM) {
    newPage();
    y = 72.5;
  }
  if (vat) {
    text(EST.LEFT, 'Total excluding ' + (meta.vat.label || 'VAT'));
    text(EST.AMT_RIGHT, estMoney(total), { right: true });
    y += EST.LINE + 2;
    text(EST.LEFT, `${meta.vat.label || 'VAT'} at ${+(vat * 100).toFixed(2)}%`);
    text(EST.AMT_RIGHT, estMoney(vatAmt), { right: true });
    y += EST.LINE + 6;
  }
  text(EST.LEFT, vat ? 'Grand Total including ' + (meta.vat.label || 'VAT') : 'Grand Total', {
    bold: true
  });
  text(EST.AMT_RIGHT, estMoney(Math.round((total + vatAmt) * 100) / 100), {
    bold: true,
    right: true
  });
  y += 28;
  text(EST.LEFT, 'E&OE', {
    bold: true
  });
  if (termLines.length) {
    y += 24;
    termLines.forEach(l => {
      if (y + EST.LINE > EST.BOTTOM - 120) { newPage(); y = 72.5; }
      if (l) text(EST.LEFT, l);
      y += EST.LINE;
    });
    y -= 24;
  }
  y += 66;
  if (y + 110 > EST.BOTTOM) {
    newPage();
    y = 72.5;
  }
  text(EST.LEFT, 'Yours sincerely');
  if (meta.signature) ops.push(signatureOp(meta.signature, y));
  y += 55;
  text(EST.LEFT, meta.signedBy || '');
  y += 15;
  text(EST.LEFT, ent.company, {
    bold: true
  });
  const missing = new Set();
  pages.flat().forEach(o => {
    if (o.t === 'text') (String(o.s).match(EST_CHARS) || []).forEach(ch => missing.add(ch));
  });
  return {
    pages,
    total,
    lines: estLines,
    net: total,
    vatAmount: vatAmt,
    grand: Math.round((total + vatAmt) * 100) / 100,
    ccy: CUR.bill,
    missing: [...missing]
  };
}
// A change order: the same letter as the estimate, listing only what changed since the client last approved
// (core/approvals.js), each with the change in the amount column, then the approved total, this change and the
// revised total. `base` is what was approved ({ total, label, date, po }).
export function buildChangeOrder({ changes, base, meta }) {
  const ent = EST_ENTITIES[meta.entity] || EST_ENTITIES.glassbox;
  const pages = [];
  let ops, y;
  const newPage = () => {
    ops = [];
    if (ent.letterhead) ops.push({ t: 'img', key: 'strip', ...EST.STRIP });
    else ops.push({ t: 'text', x: 470, y: 65.2 + EST.ASC, s: ent.company, bold: true });
    pages.push(ops);
    y = 65.2;
  };
  const text = (x, s, o = {}) => ops.push({ t: 'text', x, y: y + EST.ASC, s, ...o });
  const para = (s, width, o = {}) => estWrap(s, width, !!o.bold).forEach(l => {
    if (y + EST.LINE > EST.BOTTOM) { newPage(); y = 72.5; }
    text(EST.LEFT, l, o); y += EST.LINE;
  });
  const signed = n => (n > 0 ? '+' : '') + estMoney(n);
  const r2 = n => Math.round(n * 100) / 100;
  newPage();
  text(EST.LEFT, ordinalDate(meta.date));
  y = 120.4;
  String(meta.recipient || '').split('\n').map(l => l.trim()).filter(Boolean).forEach(l => { text(EST.LEFT, l); y += EST.LINE; });
  y = Math.max(272.6, y + 40);
  const titleLines = estWrap(meta.title || '', EST.TITLE_W, true);
  text(EST.LEFT, 'Change order:', { bold: true });
  text(EST.CCY_X, ccyHeader(CUR.bill), { bold: true });
  titleLines.forEach((l, i) => ops.push({ t: 'text', x: EST.TITLE_X, y: y + EST.ASC + i * EST.LINE, s: l, bold: true }));
  y += 27.1 + (titleLines.length - 1) * EST.LINE;
  const approved = [base.label ? `our estimate ${base.label}` : 'our estimate', base.date ? `approved on ${ordinalDate(base.date)}` : 'as approved',
    base.po ? `(purchase order ${base.po})` : ''].filter(Boolean).join(' ');
  para(`Further to ${approved}, these are the changes to the scope and their cost.`, EST.AMT_RIGHT - EST.LEFT);
  y += EST.LINE;
  let delta = 0, secName = null;
  (changes || []).forEach(c => {
    const d = r2((c.now || 0) - (c.was || 0));
    const desc = estWrap(c.desc, EST.DESC_W, false);
    const note = estWrap(c.was == null ? 'New' : c.now == null ? `Removed (was ${estMoney(c.was)})` : c.wasDesc && c.was === c.now ? `Reworded (was "${c.wasDesc}")` : `Was ${estMoney(c.was)}, now ${estMoney(c.now)}`, EST.DESC_W, false);
    const head = c.sec !== secName;
    const need = (head ? 25 + (secName == null ? 0 : EST.LINE) : 0) + (desc.length + note.length) * EST.LINE + 6;
    if (y + need > EST.BOTTOM) { newPage(); y = 72.5; secName = null; }
    if (c.sec !== secName) {
      if (secName != null) y += EST.LINE;
      text(EST.LEFT, c.sec || '', { bold: true }); y += 25; secName = c.sec;
    }
    desc.forEach((l, i) => ops.push({ t: 'text', x: EST.LEFT, y: y + EST.ASC + i * EST.LINE, s: l }));
    text(EST.AMT_RIGHT, signed(d), { right: true });
    y += desc.length * EST.LINE;
    note.forEach(l => { text(EST.LEFT + 12, l); y += EST.LINE; });
    y += 6;
    delta = r2(delta + d);
  });
  y += 13.5;
  if (y + 190 > EST.BOTTOM) { newPage(); y = 72.5; }
  const vat = meta.vat && meta.vat.on && Number(meta.vat.rate) > 0 ? meta.vat : null;
  const newTotal = r2(base.total + delta);
  text(EST.LEFT, 'Approved to date'); text(EST.AMT_RIGHT, estMoney(base.total), { right: true }); y += EST.LINE + 2;
  text(EST.LEFT, 'This change order'); text(EST.AMT_RIGHT, signed(delta), { right: true }); y += EST.LINE + 6;
  text(EST.LEFT, 'Revised total', { bold: true }); text(EST.AMT_RIGHT, estMoney(newTotal), { bold: true, right: true });
  y += 28;
  if (vat) { para(`All amounts exclude ${vat.label || 'VAT'}, charged at ${+Number(vat.rate).toFixed(2)}%.`, EST.AMT_RIGHT - EST.LEFT); y += 8; }
  text(EST.LEFT, 'E&OE', { bold: true });
  y += 24;
  para('Please confirm your approval of this change order and, where needed, raise a purchase order for the change.', EST.AMT_RIGHT - EST.LEFT);
  y += 40;
  if (y + 110 > EST.BOTTOM) { newPage(); y = 72.5; }
  text(EST.LEFT, 'Yours sincerely');
  if (meta.signature) ops.push(signatureOp(meta.signature, y));
  y += 55;
  text(EST.LEFT, meta.signedBy || '');
  y += 15;
  text(EST.LEFT, ent.company, { bold: true });
  const missing = new Set();
  pages.flat().forEach(o => { if (o.t === 'text') (String(o.s).match(EST_CHARS) || []).forEach(ch => missing.add(ch)); });
  return { pages, total: delta, changes, baseTotal: base.total, newTotal, ccy: CUR.bill, missing: [...missing] };
}
// Scales the estimate pages to the space available, so the whole page is always visible.
export function FitEstimate({ layout }) {
  const ref = useRef(null);
  const [w, setW] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => setW(el.clientWidth);
    measure();
    const ro = window.ResizeObserver ? new ResizeObserver(measure) : null;
    if (ro) ro.observe(el); else window.addEventListener('resize', measure);
    return () => { if (ro) ro.disconnect(); else window.removeEventListener('resize', measure); };
  }, []);
  const scale = w ? Math.max(0.45, Math.min(1.5, (w - 4) / EST.W)) : 0;
  return React.createElement("div", {
    ref,
    style: { width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }
  }, scale > 0 && React.createElement(EstimatePages, { layout, scale }));
}
function EstimatePages({
  layout,
  scale = 1.5
}) {
  return layout.pages.map((ops, pi) => React.createElement("div", {
    key: pi,
    style: {
      width: EST.W * scale,
      height: EST.H * scale,
      background: '#fff',
      position: 'relative',
      overflow: 'hidden',
      boxShadow: '0 4px 32px rgba(62,63,67,.18)',
      marginBottom: 24,
      flexShrink: 0,
      color: '#000',
      fontFamily: 'Arial, "Liberation Sans", Arimo, Helvetica, sans-serif'
    }
  }, ops.map((o, i) => o.t === 'img' ? React.createElement("img", {
    key: i,
    src: o.src || asset(o.key),
    alt: "",
    style: {
      position: 'absolute',
      left: o.x * scale,
      top: o.y * scale,
      width: o.w * scale,
      height: o.h * scale
    }
  }) : React.createElement("div", {
    key: i,
    style: {
      position: 'absolute',
      top: (o.y - EST.ASC - 1.4) * scale,
      whiteSpace: 'pre',
      lineHeight: 1.15,
      fontSize: EST.SIZE * scale,
      fontWeight: o.bold ? 700 : 400,
      ...(o.right ? {
        right: (EST.W - o.x) * scale
      } : {
        left: o.x * scale
      })
    }
  }, o.s))));
}
export function downloadEstimatePDF(layout, filename) {
  const {
    jsPDF
  } = window.jspdf;
  const pdf = new jsPDF({
    unit: 'pt',
    format: 'a4',
    compress: true
  });
  estRegisterFont(pdf);
  pdf.setProperties({
    title: filename.replace(/\.pdf$/, '')
  });
  layout.pages.forEach((ops, pi) => {
    if (pi) pdf.addPage('a4', 'portrait');
    ops.forEach(o => {
      if (o.t === 'img') {
        const src = o.src || asset(o.key);
        pdf.addImage(src, src.startsWith('data:image/png') ? 'PNG' : 'JPEG', o.x, o.y, o.w, o.h, o.key, 'FAST');
      } else {
        pdf.setFont('EstSans', o.bold ? 'bold' : 'normal');
        pdf.setFontSize(EST.SIZE);
        pdf.text(o.s, o.x, o.y, o.right ? {
          align: 'right'
        } : undefined);
      }
    });
  });
  audit('estimate.pdf', { file: filename });
  pdf.save(filename);
}
